import {GameColor} from '../../GameColor'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {UIGameManager} from '../UIGameManager'
import {Ease, PIXI} from '@colonist/pixi'
import {TurnState} from '@colonist/game-states'
import {Point} from '@colonist/game-mechanic-validators'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {UIGameExpandContractAnimation} from '../../ui_canvas_engine/animation/UIGameExpandContractAnimation'

export interface UIGameDice {
    icon: SpriteView
    center: Point
    animation?: UIGameExpandContractAnimation
}

export abstract class UIGameDiceView extends SectionBgView {
    private readonly animationDistanceX: number = 20
    protected readonly uiGameManager: UIGameManager

    private readonly diceViews: UIGameDice[]
    private readonly container: PIXI.Container

    protected isDiceNumberAnimationEnabled: boolean = false

    private readonly ease: Ease
    private readonly easeOutX: number
    private readonly easeInX: number

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, diceSize: number, container: PIXI.Container, isMyDice: boolean = true) {
        super(x, y, w, diceSize, container, true)
        this.uiGameManager = uiGameManager
        this.container = container
        this.diceViews = []

        const shouldAnimateFromLeft = this.shouldAnimateFromLeftForMobile()
        this.ease = new Ease({useRef: false})
        this.easeInX = x
        this.easeOutX = x + this.animationDistanceX * (shouldAnimateFromLeft ? -1 : 1)

        if(!isMyDice) return

        this.hide()
    }

    abstract updateDiceTexture()

    protected addDice(diceView: SpriteView) {
        const center = new Point(diceView.x + diceView.width / 2, diceView.y + diceView.height / 2)
        const dice: UIGameDice = {icon: diceView, center: center}
        this.diceViews.push(dice)
    }

    animate() {
        this.diceViews.forEach((dice: UIGameDice) => {
            dice.animation = new UIGameExpandContractAnimation(dice.icon)
            dice.animation.start()
        })
    }

    resetDice() {
        this.diceViews.forEach((dice: UIGameDice) => dice.animation?.stop())
        this.tintPassive()
    }

    tintActive() {
        if(this.uiGameManager.gameController.isReplay) return
        this.diceViews.forEach((dice: UIGameDice) => dice.icon.tint = GameColor.getColorHexValue(GameColor.Color.White))
    }

    tintPassive() {
        this.diceViews.forEach((dice: UIGameDice) => dice.icon.tint = GameColor.getColorHexValue(GameColor.Color.Gray2))
    }

    hide() {
        if(this.destroyed) return

        this.x = this.initialPosition.x
        this.y = this.initialPosition.y
        if(this.parent == undefined) return
        this.parent.removeChild(this)
    }

    show() {
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement) return
        if(this.parent != undefined) return
        this.container.addChild(this)
    }

    private shouldAnimateFromLeftForMobile() {
        const mobileUser = UIUtilsEjsVariables.isUserOnMobileAppOrWeb && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI && !UIUtilsEjsVariables.isUserOnTabletApp
        const isPlayerViewOnLeft = this.x > this.uiGameManager.canvasController.appW / 4
        const shouldAnimateFromLeft = mobileUser && isPlayerViewOnLeft
        return shouldAnimateFromLeft
    }

    easeOut(animationTime: number) {
        if(this.parent == undefined) return
        if(this.uiGameManager.gameState.players.length == 1) return this.hide()
        this.x = this.easeInX

        this.ease.removeAll()
        const animation = this.ease.add(this, {alpha: 0, x: this.easeOutX, y: this.y}, {duration: animationTime, ease: 'easeOutQuad'})

        // Jeff: hide and show animations need to be in callbacks to queue animations properly and avoid race conditions
        animation.on('complete', () => this.hide())
    }

    easeIn(animationTime: number) {
        this.show()
        this.x = this.easeOutX

        this.ease.removeAll()
        const animation = this.ease.add(this, {alpha: 1, x: this.easeInX, y: this.y}, {duration: animationTime, ease: 'easeOutQuad'})

        // Jeff: hide and show animations need to be in callbacks to queue animations properly and avoid race conditions
        animation.on('complete', () => this.show())
    }

    onCompleteCallback(callback: () => void) {
        this.ease.on('complete', callback)
    }
}
